import { useRef, useState, type ReactNode } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useIsMobile } from "../../hooks/useIsMobile";
import { MIN_TAP_TARGET } from "../../styles/responsive";

interface Props {
  /** 検索条件の入力欄 */
  children: ReactNode;
  /**
   * 検索実行。`false`（または `false` で解決する Promise）を返すと SP のダイアログを閉じない。
   * 入力検証付き（React Hook Form の handleSubmit 等）は、検証成功時だけ `true` を返すこと。
   * 通信失敗などのエラー通知（トースト等）は呼び出し側の責務。この部品は reject を吸収して
   * 入力を残すだけで通知しない。呼び出し側で catch して通知し、`false` を返すこと。
   */
  onSearch: () => unknown;
  onClear: () => void;
  /** SP のダイアログ開始ボタンの文言 */
  triggerLabel?: string;
  /** 設定中の条件数。SP のボタンに表示する */
  activeCount?: number;
}

/**
 * 検索条件の器。
 * - PC: 画面内にインライン表示（Paper）
 * - SP: 「検索条件」ボタンからフルスクリーンの Dialog で開く。検索実行で閉じる
 *
 * 入力値の保持は呼び出し側の責務。Dialog は閉じると children をアンマウントし、
 * PC/SP 切替でも再マウントされるため、条件は親の state で持ち value/onChange で渡す
 * （クリア・検索・activeCount も親の state に接続する）。見本は UiCatalogContent を参照。
 */
export const ResponsiveSearchPanel: React.FC<Props> = ({
  children,
  onSearch,
  onClear,
  triggerLabel = "検索条件",
  activeCount = 0,
}) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  // 実行中は再送信を止め、開閉サイクルごとの番号で古い検索の完了を無視する
  const [searching, setSearching] = useState(false);
  const cycleRef = useRef(0);

  const close = (): void => {
    cycleRef.current += 1;
    setSearching(false);
    setOpen(false);
  };

  const runSearch = async (): Promise<void> => {
    const cycle = cycleRef.current;
    setSearching(true);
    try {
      const result: unknown = await onSearch();
      if (cycle !== cycleRef.current) return;
      setSearching(false);
      if (result !== false) close();
    } catch {
      // 失敗時は閉じず、入力を残して再試行できるようにする
      if (cycle === cycleRef.current) setSearching(false);
    }
  };

  // SP のダイアログは PageContainer の縮小スタイルの外にあるため、タップ領域をここで確保する
  const tapSx = isMobile ? { minHeight: MIN_TAP_TARGET } : undefined;

  const actions = (
    <>
      <Button variant="outlined" sx={tapSx} onClick={onClear}>
        クリア
      </Button>
      <Button
        variant="contained"
        startIcon={<SearchIcon />}
        sx={tapSx}
        disabled={isMobile && searching}
        onClick={() => {
          void runSearch();
        }}
      >
        検索
      </Button>
    </>
  );

  if (!isMobile) {
    return (
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2}>
          {children}
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {actions}
          </Stack>
        </Stack>
      </Paper>
    );
  }

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<SearchIcon />}
        onClick={() => {
          setOpen(true);
        }}
        sx={{ minHeight: MIN_TAP_TARGET, alignSelf: "flex-start" }}
      >
        {activeCount > 0 ? `${triggerLabel}（${activeCount}）` : triggerLabel}
      </Button>
      <Dialog
        fullScreen
        open={open}
        // 入力中の誤タップで条件が消えないよう、背景タップ・Esc では閉じない
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
          close();
        }}
      >
        <DialogTitle>{triggerLabel}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {children}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button sx={tapSx} onClick={close}>
            閉じる
          </Button>
          {actions}
        </DialogActions>
      </Dialog>
    </>
  );
};
